import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { useTracking } from '../../hooks/useTracking';
import { playerProfileService } from '../../services/playerMasterService';
import { useAuth } from '../../contexts/AuthContext';
import { realtimeMonitoringService } from '../../services/realtimeMonitoringService';

const PlayerRegistrationForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    logError,
    logActivity,
    logFormFieldChange,
    logFormFieldBlur,
    logValidationError,
    logFormSubmit,
    logPerformance
  } = useTracking('player-registration-form');
  const [formData, setFormData] = useState({
    // Basic Identity
    fullName: '',
    knownAs: '',
    dateOfBirth: '',
    placeOfBirth: '',
    nationalityPrimary: '',
    nationalitySecondary: '',
    height: '',
    weight: '',
    preferredFoot: '',
    primaryPosition: '',
    secondaryPositions: '',
    
    // Contact Information
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    
    // Current Status
    currentClub: '',
    currentLeague: '',
    currentCountry: '',
    squadNumber: '',
    contractType: '',
    contractStartDate: '',
    contractEndDate: '',
    
    // Transfer Status
    availableForTransfer: '',
    availableForLoan: '',
    currentMarketValue: '',
    
    // Agent & Representation
    agentName: '',
    agencyName: '',
    agentEmail: '',
    agentPhone: '',
    representationContractUntil: '',
    
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    
    // Additional Information
    previousClubs: '',
    achievements: '',
    medicalConditions: '',
    additionalNotes: ''
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [marketValueUnit, setMarketValueUnit] = useState('millions');

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    logFormFieldChange(name, value);
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    
    logFormFieldBlur(fieldName);
    
    // Basic validation logic
    const newErrors = { ...errors };
    
    if (fieldName === 'email' && formData?.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email address';
        logValidationError('email', 'Invalid email format');
      } else {
        delete newErrors?.email;
      }
    }
    
    if (fieldName === 'phone' && formData?.phone) {
      const phoneRegex = /^\+?[\d\s-()]+$/;
      if (!phoneRegex?.test(formData?.phone) || formData?.phone?.replace(/\D/g, '')?.length < 10) {
        newErrors.phone = 'Please enter a valid phone number';
        logValidationError('phone', 'Invalid phone number format');
      } else {
        delete newErrors?.phone;
      }
    }
    
    if (fieldName === 'squadNumber' && formData?.squadNumber) {
      const num = parseInt(formData?.squadNumber);
      if (num < 1 || num > 99) {
        newErrors.squadNumber = 'Squad number must be between 1 and 99';
        logValidationError('squadNumber', 'Squad number out of range');
      } else {
        delete newErrors?.squadNumber;
      }
    }
    
    if (fieldName === 'contractStartDate' && fieldName === 'contractEndDate') {
      if (formData?.contractStartDate && formData?.contractEndDate) {
        if (new Date(formData.contractStartDate) >= new Date(formData.contractEndDate)) {
          newErrors.contractStartDate = 'Start date must be before end date';
          newErrors.contractEndDate = 'End date must be after start date';
          logValidationError('contractDates', 'Invalid contract date range');
        } else {
          delete newErrors?.contractStartDate;
          delete newErrors?.contractEndDate;
        }
      }
    }
    
    setErrors(newErrors);
  };

  const handlePhotoChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader?.result);
      };
      reader?.readAsDataURL(file);
      logFormFieldChange('profilePhoto', file?.name);
    }
  };

  const handleClearForm = () => {
    setFormData({
      fullName: '',
      knownAs: '',
      dateOfBirth: '',
      placeOfBirth: '',
      nationalityPrimary: '',
      nationalitySecondary: '',
      height: '',
      weight: '',
      preferredFoot: '',
      primaryPosition: '',
      secondaryPositions: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      postalCode: '',
      currentClub: '',
      currentLeague: '',
      currentCountry: '',
      squadNumber: '',
      contractType: '',
      contractStartDate: '',
      contractEndDate: '',
      availableForTransfer: '',
      availableForLoan: '',
      currentMarketValue: '',
      agentName: '',
      agencyName: '',
      agentEmail: '',
      agentPhone: '',
      representationContractUntil: '',
      emergencyContactName: '',
      emergencyContactRelation: '',
      emergencyContactPhone: '',
      previousClubs: '',
      achievements: '',
      medicalConditions: '',
      additionalNotes: ''
    });
    setTouched({});
    setErrors({});
    setProfilePhoto(null);
    setPhotoPreview(null);
  };

  const handleSubmit = async () => {
    const startTime = performance.now();
    setIsSubmitting(true);

    // Start monitoring database write operation
    const dbOperation = await realtimeMonitoringService?.trackPlayerRegistrationSave({
      userId: user?.id,
      fullName: formData?.fullName,
      primaryPosition: formData?.primaryPosition,
      photoFile: profilePhoto
    });

    try {
      // Validate required fields
      const requiredFields = ['fullName', 'dateOfBirth', 'nationalityPrimary', 'preferredFoot', 'primaryPosition'];
      const missingFields = requiredFields?.filter(field => !formData?.[field]);
      
      if (missingFields?.length > 0) {
        const errorMsg = `Please fill in required fields: ${missingFields?.join(', ')}`;
        logValidationError('form_submit', errorMsg);
        
        // Track validation failure
        await dbOperation?.complete(false, new Error(errorMsg));
        
        alert(errorMsg);
        setIsSubmitting(false);
        return;
      }

      // Prepare player data
      const playerData = {
        userId: user?.id,
        fullName: formData?.fullName,
        knownAs: formData?.knownAs || null,
        dateOfBirth: formData?.dateOfBirth,
        placeOfBirth: formData?.placeOfBirth || null,
        nationalityPrimary: formData?.nationalityPrimary,
        nationalitySecondary: formData?.nationalitySecondary || null,
        height: formData?.height || null,
        weight: formData?.weight || null,
        preferredFoot: formData?.preferredFoot,
        primaryPosition: formData?.primaryPosition,
        currentClub: formData?.currentClub || null,
        currentLeague: formData?.currentLeague || null,
        currentCountry: formData?.currentCountry || null,
        squadNumber: formData?.squadNumber || null,
        contractType: formData?.contractType || null,
        contractStartDate: formData?.contractStartDate || null,
        contractEndDate: formData?.contractEndDate || null,
        availableForTransfer: formData?.availableForTransfer === 'Yes',
        availableForLoan: formData?.availableForLoan === 'Yes',
        currentMarketValue: formData?.currentMarketValue || null,
        agentName: formData?.agentName || null,
        agencyName: formData?.agencyName || null,
        agentEmail: formData?.agentEmail || null,
        agentPhone: formData?.agentPhone || null,
        photoFile: profilePhoto,
        verifiedStatus: false,
        fifaVerified: false,
        profileVisibility: 'public'
      };

      logFormSubmit('player-registration', playerData);

      // Log detailed save attempt
      await realtimeMonitoringService?.trackDatabaseWrite({
        type: 'create',
        tableName: 'player_master_profiles',
        userId: user?.id,
        metadata: {
          operation: 'player_registration_save',
          playerName: formData?.fullName,
          position: formData?.primaryPosition,
          hasPhoto: !!profilePhoto,
          formDataSize: JSON.stringify(formData)?.length
        }
      });

      // Save to database
      const result = await playerProfileService?.create(playerData);

      if (result?.error) {
        const error = new Error(result.error.message || 'Failed to save player profile');
        
        // Track database write failure with detailed error
        await dbOperation?.complete(false, error);
        
        // Log detailed error information
        await realtimeMonitoringService?.trackDisplayFailure({
          componentName: 'PlayerRegistrationForm',
          type: 'database_save_failed',
          error,
          userId: user?.id,
          metadata: {
            playerName: formData?.fullName,
            errorDetails: result?.error,
            attemptedData: {
              fullName: formData?.fullName,
              position: formData?.primaryPosition,
              hasPhoto: !!profilePhoto
            }
          }
        });
        
        throw error;
      }

      // Track successful database write
      await dbOperation?.complete(true, null, result?.data?.id);

      const endTime = performance.now();
      logPerformance('form_submission', endTime - startTime, true);
      logActivity('player_registration_completed', { 
        playerId: result?.data?.id,
        duration: endTime - startTime,
        success: true
      });

      // Log successful save with monitoring
      await realtimeMonitoringService?.trackDatabaseWrite({
        type: 'create',
        tableName: 'player_master_profiles',
        recordId: result?.data?.id,
        userId: user?.id,
        metadata: {
          operation: 'player_registration_complete',
          playerName: formData?.fullName,
          playerId: result?.data?.id,
          duration: endTime - startTime
        }
      });

      alert('Player profile created successfully! Redirecting to player profiles...');
      navigate('/player-profiles');
    } catch (error) {
      console.error('Error saving player profile:', error);
      
      // Track the error with monitoring service
      await dbOperation?.complete(false, error);
      
      logError(error, { 
        context: 'player_registration_submit',
        playerName: formData?.fullName,
        errorType: error?.name,
        errorMessage: error?.message
      });
      
      // Track display failure
      await realtimeMonitoringService?.trackDisplayFailure({
        componentName: 'PlayerRegistrationForm',
        type: 'save_operation_failed',
        error,
        userId: user?.id,
        metadata: {
          playerName: formData?.fullName,
          position: formData?.primaryPosition,
          errorStack: error?.stack
        }
      });
      
      alert(`Error: ${error?.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc?.internal?.pageSize?.getWidth();
    const pageHeight = doc?.internal?.pageSize?.getHeight();
    const margin = 20;
    const lineHeight = 7;
    let yPosition = margin;

    // Helper function to add text with line wrapping
    const addText = (text, x, y, maxWidth) => {
      const lines = doc?.splitTextToSize(text, maxWidth);
      doc?.text(lines, x, y);
      return lines?.length * lineHeight;
    };

    // Helper function to check if new page is needed
    const checkNewPage = (requiredSpace) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        doc?.addPage();
        yPosition = margin;
      }
    };

    // Header
    doc?.setFontSize(20);
    doc?.setFont('helvetica', 'bold');
    doc?.text('PLAYER REGISTRATION FORM', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.text('ProX11Market - Professional Football Player Registration', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Horizontal line
    doc?.setLineWidth(0.5);
    doc?.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Section helper
    const addSection = (title, fields) => {
      checkNewPage(20);
      doc?.setFontSize(12);
      doc?.setFont('helvetica', 'bold');
      doc?.text(title, margin, yPosition);
      yPosition += 8;

      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');

      fields?.forEach(field => {
        checkNewPage(15);
        doc?.text(`${field?.label}:`, margin, yPosition);
        
        // Draw underline for filling
        const underlineY = yPosition + 1;
        doc?.setLineWidth(0.3);
        doc?.line(margin + 60, underlineY, pageWidth - margin, underlineY);
        
        // Add value if exists
        if (field?.value) {
          doc?.setFont('helvetica', 'bold');
          doc?.text(field?.value, margin + 62, yPosition);
          doc?.setFont('helvetica', 'normal');
        }
        
        yPosition += lineHeight + 2;
      });
      
      yPosition += 5;
    };

    // SECTION 1: Basic Identity
    addSection('1. BASIC IDENTITY INFORMATION', [
      { label: 'Full Legal Name', value: formData?.fullName },
      { label: 'Known As (Nickname)', value: formData?.knownAs },
      { label: 'Date of Birth (DD/MM/YYYY)', value: formData?.dateOfBirth },
      { label: 'Place of Birth', value: formData?.placeOfBirth },
      { label: 'Primary Nationality', value: formData?.nationalityPrimary },
      { label: 'Secondary Nationality', value: formData?.nationalitySecondary },
      { label: 'Height (cm)', value: formData?.height },
      { label: 'Weight (kg)', value: formData?.weight },
      { label: 'Preferred Foot', value: formData?.preferredFoot },
      { label: 'Primary Position', value: formData?.primaryPosition },
      { label: 'Secondary Positions', value: formData?.secondaryPositions }
    ]);

    // SECTION 2: Contact Information
    addSection('2. CONTACT INFORMATION', [
      { label: 'Email Address', value: formData?.email },
      { label: 'Phone Number', value: formData?.phone },
      { label: 'Street Address', value: formData?.address },
      { label: 'City', value: formData?.city },
      { label: 'Country', value: formData?.country },
      { label: 'Postal Code', value: formData?.postalCode }
    ]);

    // SECTION 3: Current Club Status
    addSection('3. CURRENT CLUB STATUS', [
      { label: 'Current Club', value: formData?.currentClub },
      { label: 'Current League', value: formData?.currentLeague },
      { label: 'Current Country', value: formData?.currentCountry },
      { label: 'Squad Number', value: formData?.squadNumber },
      { label: 'Contract Type', value: formData?.contractType },
      { label: 'Contract Start Date', value: formData?.contractStartDate },
      { label: 'Contract End Date', value: formData?.contractEndDate }
    ]);

    // SECTION 4: Transfer Status
    addSection('4. TRANSFER STATUS', [
      { label: 'Available for Transfer', value: formData?.availableForTransfer },
      { label: 'Available for Loan', value: formData?.availableForLoan },
      { label: 'Current Market Value (EUR)', value: formData?.currentMarketValue }
    ]);

    // SECTION 5: Agent & Representation
    addSection('5. AGENT & REPRESENTATION', [
      { label: 'Agent Name', value: formData?.agentName },
      { label: 'Agency Name', value: formData?.agencyName },
      { label: 'Agent Email', value: formData?.agentEmail },
      { label: 'Agent Phone', value: formData?.agentPhone },
      { label: 'Representation Contract Until', value: formData?.representationContractUntil }
    ]);

    // SECTION 6: Emergency Contact
    addSection('6. EMERGENCY CONTACT', [
      { label: 'Emergency Contact Name', value: formData?.emergencyContactName },
      { label: 'Relationship', value: formData?.emergencyContactRelation },
      { label: 'Emergency Phone Number', value: formData?.emergencyContactPhone }
    ]);

    // SECTION 7: Career History
    checkNewPage(30);
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.text('7. CAREER HISTORY', margin, yPosition);
    yPosition += 8;
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.text('Previous Clubs (List all clubs with years):', margin, yPosition);
    yPosition += 7;
    
    // Multi-line text area
    for (let i = 0; i < 5; i++) {
      checkNewPage(10);
      doc?.line(margin + 10, yPosition, pageWidth - margin, yPosition);
      if (i === 0 && formData?.previousClubs) {
        const lines = doc?.splitTextToSize(formData?.previousClubs, pageWidth - margin - 20);
        doc?.text(lines, margin + 12, yPosition - 2);
      }
      yPosition += 7;
    }
    yPosition += 5;

    // SECTION 8: Achievements
    checkNewPage(30);
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.text('8. ACHIEVEMENTS & HONORS', margin, yPosition);
    yPosition += 8;
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.text('List major achievements, awards, and honors:', margin, yPosition);
    yPosition += 7;
    
    for (let i = 0; i < 5; i++) {
      checkNewPage(10);
      doc?.line(margin + 10, yPosition, pageWidth - margin, yPosition);
      if (i === 0 && formData?.achievements) {
        const lines = doc?.splitTextToSize(formData?.achievements, pageWidth - margin - 20);
        doc?.text(lines, margin + 12, yPosition - 2);
      }
      yPosition += 7;
    }
    yPosition += 5;

    // SECTION 9: Medical Information
    checkNewPage(30);
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.text('9. MEDICAL INFORMATION', margin, yPosition);
    yPosition += 8;
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    doc?.text('Medical conditions, injuries, or allergies:', margin, yPosition);
    yPosition += 7;
    
    for (let i = 0; i < 4; i++) {
      checkNewPage(10);
      doc?.line(margin + 10, yPosition, pageWidth - margin, yPosition);
      if (i === 0 && formData?.medicalConditions) {
        const lines = doc?.splitTextToSize(formData?.medicalConditions, pageWidth - margin - 20);
        doc?.text(lines, margin + 12, yPosition - 2);
      }
      yPosition += 7;
    }
    yPosition += 5;

    // SECTION 10: Additional Notes
    checkNewPage(30);
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.text('10. ADDITIONAL NOTES', margin, yPosition);
    yPosition += 8;
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'normal');
    
    for (let i = 0; i < 4; i++) {
      checkNewPage(10);
      doc?.line(margin + 10, yPosition, pageWidth - margin, yPosition);
      if (i === 0 && formData?.additionalNotes) {
        const lines = doc?.splitTextToSize(formData?.additionalNotes, pageWidth - margin - 20);
        doc?.text(lines, margin + 12, yPosition - 2);
      }
      yPosition += 7;
    }
    yPosition += 10;

    // Signature Section
    checkNewPage(40);
    doc?.setLineWidth(0.5);
    doc?.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.text('DECLARATION', margin, yPosition);
    yPosition += 7;

    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    const declaration = 'I hereby declare that the information provided above is true and accurate to the best of my knowledge. I understand that any false information may result in the rejection of my application or termination of any agreement.';
    const declarationLines = doc?.splitTextToSize(declaration, pageWidth - 2 * margin);
    doc?.text(declarationLines, margin, yPosition);
    yPosition += declarationLines?.length * 5 + 10;

    checkNewPage(40);
    
    // Signature lines
    const signatureY = yPosition;
    doc?.setFontSize(10);
    doc?.text('Player Signature:', margin, signatureY);
    doc?.line(margin + 40, signatureY, margin + 90, signatureY);
    
    doc?.text('Date:', pageWidth - margin - 60, signatureY);
    doc?.line(pageWidth - margin - 40, signatureY, pageWidth - margin, signatureY);
    
    yPosition += 15;
    doc?.text('Parent/Guardian Signature (if under 18):', margin, yPosition);
    doc?.line(margin + 75, yPosition, margin + 125, yPosition);
    
    doc?.text('Date:', pageWidth - margin - 60, yPosition);
    doc?.line(pageWidth - margin - 40, yPosition, pageWidth - margin, yPosition);

    // Footer
    const footerY = pageHeight - 15;
    doc?.setFontSize(8);
    doc?.setTextColor(128, 128, 128);
    doc?.text('ProX11Market - Professional Football Player Registration', pageWidth / 2, footerY, { align: 'center' });
    doc?.text('For official use only - Confidential', pageWidth / 2, footerY + 4, { align: 'center' });

    // Save PDF
    const fileName = formData?.fullName 
      ? `Player_Registration_${formData?.fullName?.replace(/\s+/g, '_')}.pdf`
      : 'Player_Registration_Form.pdf';
    doc?.save(fileName);
  };

  const positions = [
    'Goalkeeper',
    'Right Back',
    'Left Back',
    'Center Back',
    'Defensive Midfielder',
    'Central Midfielder',
    'Attacking Midfielder',
    'Right Winger',
    'Left Winger',
    'Striker',
    'Forward'
  ];

  const contractTypes = [
    'Permanent',
    'Loan',
    'Free Agent',
    'Youth Contract',
    'Professional Contract'
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-24 pb-16 px-4 md:px-6 lg:px-8 max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Back</span>
          </button>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Player Registration Form</h1>
              <p className="text-muted-foreground">Complete the form and generate a PDF for physical or digital submission</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Button
            onClick={generatePDF}
            className="flex items-center gap-2"
          >
            <Icon name="Download" size={18} />
            Generate PDF Form
          </Button>
          <Button
            onClick={handleClearForm}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Icon name="RotateCcw" size={18} />
            Clear Form
          </Button>
        </div>

        {/* Info Alert */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex gap-3">
            <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">How to use this form</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Fill in the fields below (optional - you can generate a blank form)</li>
                <li>• Click "Generate PDF Form" to download the form</li>
                <li>• The PDF can be printed and filled by hand or filled digitally</li>
                <li>• All fields in the PDF have space for manual entry</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form Sections */}
        <div className="space-y-8">
          {/* Section 1: Basic Identity */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="User" size={20} className="text-primary" />
              1. Basic Identity Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Profile Photo
                </label>
                <div className="flex items-center gap-4">
                  {photoPreview && (
                    <img
                      src={photoPreview}
                      alt="Profile preview"
                      className="w-24 h-24 rounded-full object-cover border-2 border-primary"
                    />
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Upload a professional headshot (JPG, PNG, max 5MB)
                    </p>
                  </div>
                </div>
              </div>
              <Input
                label="Full Legal Name *"
                name="fullName"
                value={formData?.fullName}
                onChange={handleInputChange}
                onBlur={() => handleBlur('fullName')}
                placeholder="Enter full legal name"
                required
              />
              <Input
                label="Known As (Nickname)"
                name="knownAs"
                value={formData?.knownAs}
                onChange={handleInputChange}
                placeholder="Enter nickname"
              />
              <Input
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={formData?.dateOfBirth}
                onChange={handleInputChange}
              />
              <Input
                label="Place of Birth"
                name="placeOfBirth"
                value={formData?.placeOfBirth}
                onChange={handleInputChange}
                placeholder="City, Country"
              />
              <Input
                label="Primary Nationality"
                name="nationalityPrimary"
                value={formData?.nationalityPrimary}
                onChange={handleInputChange}
                placeholder="Enter nationality"
              />
              <Input
                label="Secondary Nationality"
                name="nationalitySecondary"
                value={formData?.nationalitySecondary}
                onChange={handleInputChange}
                placeholder="Enter secondary nationality (if any)"
              />
              <Input
                label="Height (cm)"
                name="height"
                type="number"
                value={formData?.height}
                onChange={handleInputChange}
                placeholder="e.g., 180"
              />
              <Input
                label="Weight (kg)"
                name="weight"
                type="number"
                value={formData?.weight}
                onChange={handleInputChange}
                placeholder="e.g., 75"
              />
              <Select
                label="Preferred Foot"
                name="preferredFoot"
                value={formData?.preferredFoot}
                onChange={handleInputChange}
              >
                <option value="">Select preferred foot</option>
                <option value="Right">Right</option>
                <option value="Left">Left</option>
                <option value="Both">Both</option>
              </Select>
              <Select
                label="Primary Position"
                name="primaryPosition"
                value={formData?.primaryPosition}
                onChange={handleInputChange}
              >
                <option value="">Select primary position</option>
                {positions?.map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </Select>
              <Input
                label="Secondary Positions"
                name="secondaryPositions"
                value={formData?.secondaryPositions}
                onChange={handleInputChange}
                placeholder="e.g., CAM, RW"
                className="md:col-span-2"
              />
            </div>
          </div>

          {/* Section 2: Contact Information */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Mail" size={20} className="text-primary" />
              2. Contact Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData?.email}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('email')}
                  placeholder="player@example.com"
                  className={touched?.email && errors?.email ? 'border-red-500' : ''}
                />
                {touched?.email && errors?.email && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {errors?.email}
                  </p>
                )}
              </div>
              <div>
                <Input
                  label="Phone Number"
                  name="phone"
                  value={formData?.phone}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('phone')}
                  placeholder="+1234567890"
                  className={touched?.phone && errors?.phone ? 'border-red-500' : ''}
                />
                {touched?.phone && errors?.phone && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {errors?.phone}
                  </p>
                )}
              </div>
              <Input
                label="Street Address"
                name="address"
                value={formData?.address}
                onChange={handleInputChange}
                placeholder="Enter street address"
                className="md:col-span-2"
              />
              <Input
                label="City"
                name="city"
                value={formData?.city}
                onChange={handleInputChange}
                placeholder="Enter city"
              />
              <Input
                label="Country"
                name="country"
                value={formData?.country}
                onChange={handleInputChange}
                placeholder="Enter country"
              />
              <Input
                label="Postal Code"
                name="postalCode"
                value={formData?.postalCode}
                onChange={handleInputChange}
                placeholder="Enter postal code"
              />
            </div>
          </div>

          {/* Section 3: Current Club Status */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Shield" size={20} className="text-primary" />
              3. Current Club Status
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Current Club"
                name="currentClub"
                value={formData?.currentClub}
                onChange={handleInputChange}
                placeholder="Enter current club"
              />
              <Input
                label="Current League"
                name="currentLeague"
                value={formData?.currentLeague}
                onChange={handleInputChange}
                placeholder="Enter league name"
              />
              <Input
                label="Current Country"
                name="currentCountry"
                value={formData?.currentCountry}
                onChange={handleInputChange}
                placeholder="Enter country"
              />
              <div>
                <Input
                  label="Squad Number"
                  name="squadNumber"
                  type="number"
                  value={formData?.squadNumber}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('squadNumber')}
                  placeholder="e.g., 10"
                  className={touched?.squadNumber && errors?.squadNumber ? 'border-red-500' : ''}
                />
                {touched?.squadNumber && errors?.squadNumber && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {errors?.squadNumber}
                  </p>
                )}
              </div>
              <Select
                label="Contract Type"
                name="contractType"
                value={formData?.contractType}
                onChange={handleInputChange}
              >
                <option value="">Select contract type</option>
                {contractTypes?.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Select>
              <div>
                <Input
                  label="Contract Start Date"
                  name="contractStartDate"
                  type="date"
                  value={formData?.contractStartDate}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('contractStartDate')}
                  className={touched?.contractStartDate && errors?.contractStartDate ? 'border-red-500' : ''}
                />
                {touched?.contractStartDate && errors?.contractStartDate && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {errors?.contractStartDate}
                  </p>
                )}
              </div>
              <div>
                <Input
                  label="Contract End Date"
                  name="contractEndDate"
                  type="date"
                  value={formData?.contractEndDate}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('contractEndDate')}
                  className={touched?.contractEndDate && errors?.contractEndDate ? 'border-red-500' : ''}
                />
                {touched?.contractEndDate && errors?.contractEndDate && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <Icon name="AlertCircle" size={12} />
                    {errors?.contractEndDate}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: Transfer Status */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="TrendingUp" size={20} className="text-primary" />
              4. Transfer Status
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Available for Transfer"
                name="availableForTransfer"
                value={formData?.availableForTransfer}
                onChange={handleInputChange}
              >
                <option value="">Select option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
              <Select
                label="Available for Loan"
                name="availableForLoan"
                value={formData?.availableForLoan}
                onChange={handleInputChange}
              >
                <option value="">Select option</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Select>
              <Input
                label="Current Market Value (EUR)"
                name="currentMarketValue"
                type="number"
                value={formData?.currentMarketValue}
                onChange={handleInputChange}
                placeholder="e.g., 500000"
              />
            </div>
          </div>

          {/* Section 5: Agent & Representation */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Briefcase" size={20} className="text-primary" />
              5. Agent & Representation
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Agent Name"
                name="agentName"
                value={formData?.agentName}
                onChange={handleInputChange}
                placeholder="Enter agent name"
              />
              <Input
                label="Agency Name"
                name="agencyName"
                value={formData?.agencyName}
                onChange={handleInputChange}
                placeholder="Enter agency name"
              />
              <Input
                label="Agent Email"
                name="agentEmail"
                type="email"
                value={formData?.agentEmail}
                onChange={handleInputChange}
                placeholder="agent@example.com"
              />
              <Input
                label="Agent Phone"
                name="agentPhone"
                type="tel"
                value={formData?.agentPhone}
                onChange={handleInputChange}
                placeholder="+1234567890"
              />
              <Input
                label="Representation Contract Until"
                name="representationContractUntil"
                type="date"
                value={formData?.representationContractUntil}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Section 6: Emergency Contact */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Phone" size={20} className="text-primary" />
              6. Emergency Contact
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Emergency Contact Name"
                name="emergencyContactName"
                value={formData?.emergencyContactName}
                onChange={handleInputChange}
                placeholder="Enter contact name"
              />
              <Input
                label="Relationship"
                name="emergencyContactRelation"
                value={formData?.emergencyContactRelation}
                onChange={handleInputChange}
                placeholder="e.g., Parent, Spouse"
              />
              <Input
                label="Emergency Phone Number"
                name="emergencyContactPhone"
                type="tel"
                value={formData?.emergencyContactPhone}
                onChange={handleInputChange}
                placeholder="+1234567890"
              />
            </div>
          </div>

          {/* Section 7: Career History */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="History" size={20} className="text-primary" />
              7. Career History
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Previous Clubs (List all clubs with years)
                </label>
                <textarea
                  name="previousClubs"
                  value={formData?.previousClubs}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  placeholder="e.g., FC Barcelona Youth (2018-2020), Real Madrid B (2020-2022)"
                />
              </div>
            </div>
          </div>

          {/* Section 8: Achievements */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Award" size={20} className="text-primary" />
              8. Achievements & Honors
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  List major achievements, awards, and honors
                </label>
                <textarea
                  name="achievements"
                  value={formData?.achievements}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  placeholder="e.g., League Champion 2021, Top Scorer 2022, Player of the Year 2023"
                />
              </div>
            </div>
          </div>

          {/* Section 9: Medical Information */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Heart" size={20} className="text-primary" />
              9. Medical Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Medical conditions, injuries, or allergies
                </label>
                <textarea
                  name="medicalConditions"
                  value={formData?.medicalConditions}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  placeholder="List any medical conditions, past injuries, or allergies"
                />
              </div>
            </div>
          </div>

          {/* Section 10: Additional Notes */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="FileText" size={20} className="text-primary" />
              10. Additional Notes
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Any additional information
                </label>
                <textarea
                  name="additionalNotes"
                  value={formData?.additionalNotes}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  placeholder="Add any other relevant information"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="flex flex-wrap gap-3 mt-8">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || Object.keys(errors)?.length > 0}
            className="flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Icon name="Save" size={18} />
                Save Player Profile
              </>
            )}
          </Button>
          <Button
            onClick={generatePDF}
            variant="outline"
            className="flex items-center gap-2"
            disabled={isSubmitting}
          >
            <Icon name="Download" size={18} />
            Generate PDF Form
          </Button>
          <Button
            onClick={handleClearForm}
            variant="outline"
            className="flex items-center gap-2"
            disabled={isSubmitting}
          >
            <Icon name="RotateCcw" size={18} />
            Clear Form
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlayerRegistrationForm;